import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  useTheme,
  Button,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { BASE_URL } from "./Constants";
import api from "./components/login/interceptors";
import SkillChipsPopover from "./components/table/SkillChipPopover";
import NoActiveTemplateModal from "./components/modals/NoActiveTemplateModal";
import ScheduleInterviewModal from "./ScheduleInterviewPage";
import usePlanGate from "./hooks/usePlanGate";
import InterviewListModal from "./components/candidate/InterviewListModal";
import EditCandidateModal from "./components/modals/EditCandidateModal";
import PencilSvgIcon from "./icons/PencilSvgIcon";
import { INTERVIEW_STATUS, normalizeInterview } from "./utils/interviewApi";

const asJobList = (value) => (Array.isArray(value) ? value : []);

const mapScheduleJob = (job) => ({
  id: job.jobdesc_id || job.jd_id || job.id,
  jobdesc_id: job.jobdesc_id || job.jd_id || job.id,
  title: job.title || job.ats_title || job.jd_title || "Untitled Job",
  jd_id: job.jobdesc_id || job.jd_id || job.id,
  jd_uuid: job.jd_uuid || "",
  parsed_skills: job.parsed_skills || [],
  status: job.status || "active",
});

const linkFlag = (job) => {
  if (job?.is_linked === true || job?.linked === true || job?.is_connected === true || job?.connected === true) return true;
  if (job?.is_linked === false || job?.linked === false || job?.is_connected === false || job?.connected === false) return false;
  if (job?.candidate_jd_id || job?.mapping_id) return true;
  return null;
};

const sameJobId = (job, id) =>
  Boolean(id) && [job?.id, job?.jobdesc_id, job?.jd_id, job?.jd_uuid, job?.routeId, job?.ats_ref_id].includes(id);

const connectedJobsFrom = (data) => {
  if (!data || typeof data !== "object") return [];
  const explicit = ["linked_jds", "connected_jds", "mapped_jds", "candidate_jds"]
    .flatMap((key) => asJobList(data[key]))
    .filter((job) => linkFlag(job) !== false);
  if (explicit.length) return explicit.map(mapScheduleJob);

  const available = asJobList(data.available_jds);
  const jds = asJobList(data.jds);
  const flagged = [...available, ...jds].filter((job) => linkFlag(job) === true);
  if (flagged.length) return flagged.map(mapScheduleJob);
  return [];
};

const CandidateDetailPage = () => {
  const scheduleGate = usePlanGate();
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [selectedJobDescription, setSelectedJobDescription] = useState("");
  const [activeTab, setActiveTab] = useState("resume");
  const [jobDescriptions, setJobDescriptions] = useState([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [templateId, setTemplateId] = useState(null);
  const [standardizedResume, setStandardizedResume] = useState(null);
  const [tailoredResume, setTailoredResume] = useState(null);
  const [tailoredResumesMap, setTailoredResumesMap] = useState({}); // Map of jd_id to tailored resume
  const [activeTailoredResumeJdId, setActiveTailoredResumeJdId] = useState(null); // Track which tailored resume is active
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingTailored, setIsGeneratingTailored] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [templateError, setTemplateError] = useState("");
  const [candidateData, setCandidateData] = useState(null);
  const [isLoadingCandidate, setIsLoadingCandidate] = useState(false);
  const [actualCandidateId, setActualCandidateId] = useState(null);
  const skillsContainerRef = useRef(null);
  const jobsRequestRef = useRef(0);
  const [containerWidth, setContainerWidth] = useState(1000);
  const [showNoTemplateModal, setShowNoTemplateModal] = useState(false);
  const [isTemplateUpdateAvailable, setIsTemplateUpdateAvailable] =
    useState(false);
  const [isUpdatingTemplate, setIsUpdatingTemplate] = useState(false);
  const [interviews, setInterviews] = useState([]);
  const [hasLoadedInterviews, setHasLoadedInterviews] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isEditCandidateOpen, setIsEditCandidateOpen] = useState(false);
  const [isInterviewListOpen, setIsInterviewListOpen] = useState(false);
  const [hasLoadedJobs, setHasLoadedJobs] = useState(false);
  const [connectedJobs, setConnectedJobs] = useState([]);
  const [candidateJobIds, setCandidateJobIds] = useState([]);

  // Job the user navigated from, or the candidate's own job when opened from the full list
  const originJobId = location.state?.jobId || null;
  const originJobTitle = location.state?.jobTitle || "";

  // Track when initial data load has fully completed, to avoid flicker
  const [hasInitialLoaded, setHasInitialLoaded] = useState(false);

  // Initial loading: true until we've completed the first round of main APIs
  const isInitialLoading = !hasInitialLoaded;

  useEffect(() => {
    const updateWidth = () => {
      if (skillsContainerRef.current) {
        const availableWidth = skillsContainerRef.current.offsetWidth - 40;
        setContainerWidth(Math.max(availableWidth, 800)); 
      }
    };
    const timer = setTimeout(updateWidth, 100);
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateWidth);
    };
  }, [candidateData]);

  // Mark initial load as complete once all key async pieces have settled
  useEffect(() => {
    if (hasInitialLoaded) return;

    const candidateLoaded = !isLoadingCandidate && !!candidateData;
    const jobsLoaded =
      !actualCandidateId || // if no candidate job context, treat as done
      (!isLoadingJobs); // once jobs request has finished (success or error)
    const templateResolved =
      !!templateId || !!templateError || showNoTemplateModal;

    if (candidateLoaded && jobsLoaded && templateResolved) {
      setHasInitialLoaded(true);
    }
  }, [
    hasInitialLoaded,
    isLoadingCandidate,
    candidateData,
    actualCandidateId,
    isLoadingJobs,
    templateId,
    templateError,
    showNoTemplateModal,
  ]);

  const getResumeUrl = (resumeFilename) => {
    if (!resumeFilename) {
      console.error("Error: resumeFilename is undefined or null.");
      return null;
    }
    const fileUrl = resumeFilename;
    const lowerCaseUrl = fileUrl.toLowerCase();

    const isFullUrl = fileUrl.startsWith("http://") || fileUrl.startsWith("https://");

    if (lowerCaseUrl.endsWith(".docx") || lowerCaseUrl.endsWith(".doc")) {
      const fullUrl = isFullUrl ? fileUrl : window.location.origin + fileUrl;
      return `https://docs.google.com/gview?url=${encodeURIComponent(fullUrl)}&embedded=true`;
    }

    if (lowerCaseUrl.endsWith(".pdf")) {
      const hasFragment = fileUrl.includes("#");
      if (hasFragment) {
        return fileUrl;
      }
      return `${fileUrl}#toolbar=1&navpanes=0&scrollbar=0`;
    }
    return fileUrl;
  };

  const resumeUrl = useMemo(() => {
    if (candidateData?.s3_url) {
      return getResumeUrl(candidateData.s3_url);
    }
    return null;
  }, [candidateData]);

  const loadCandidate = useCallback(async () => {
    if (!candidateId) {
      return;
    }
    setIsLoadingCandidate(true);
    try {
      const token = localStorage.getItem("jwtToken");
      const response = await api.get(`${BASE_URL}/api/single-candidate/${candidateId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response.data || {};
      // Candidate fields may be nested under `candidate` or returned at the top level
      const candidate = data.candidate || data;
      const standardizedResumeData = data.standardized_resume || null;
      const tailoredResumesData = data.tailored_resumes || [];
      const interviewItems = candidate.interviews || data.interviews || [];
      const interviewCount = candidate.interview_count ?? data.interview_count ?? interviewItems.length;
      const fullName = candidate.name || "";
      // Set candidate data with the new structure
      setCandidateData({
        candidate_id: candidate.candidate_id || candidateId,
        name: fullName,
        email: candidate.email || "",
        has_scheduled_interview: !!candidate.has_scheduled_interview,
        interview_count: interviewCount,
        schedulable: candidate.schedulable !== false,
        unschedulable_reason: candidate.unschedulable_reason || null,
        job_title: candidate.job_title || "",
        original_resume_url: candidate.original_resume_url || "",
        parsed_skills: candidate.parsed_skills || [],
        resume_skills: candidate.parsed_skills || [],
        s3_url: candidate.original_resume_url || "",
        first_name: fullName.split(" ")[0] || "",
        last_name: fullName.split(" ").slice(1).join(" ") || "",
      });

      if (candidate.candidate_id) {
        setActualCandidateId(candidate.candidate_id);
      }

      setInterviews(interviewItems.map(normalizeInterview));
      setHasLoadedInterviews(true);
      const jobIds = [];
      const pushJobId = (value) => {
        if (Array.isArray(value)) {
          value.forEach(pushJobId);
          return;
        }
        if (value) jobIds.push(value);
      };
      pushJobId(candidate.job_id);
      pushJobId(candidate.jd_id);
      pushJobId(candidate.jd_ids);
      pushJobId(candidate.job_ids);
      pushJobId(candidate.linked_jd_ids);
      pushJobId(data.job_id);
      pushJobId(data.jd_id);
      setCandidateJobIds(jobIds);

      if (standardizedResumeData && standardizedResumeData.pdf_url) {
        setStandardizedResume(standardizedResumeData);
        const needsUpdate =
          standardizedResumeData.is_latest_template === false ||
          standardizedResumeData.is_latest_template === "false";
        setIsTemplateUpdateAvailable(needsUpdate);
      } else {
        setIsTemplateUpdateAvailable(false);
      }
      const tailoredMap = {};
      tailoredResumesData.forEach((item) => {
        if (item.jd_id && item.resume && item.resume.pdf_url) {
          tailoredMap[item.jd_id] = {
            resume: item.resume,
            jd_details: item.jd_details || {},
            jd_id: item.jd_id,
          };
        }
      });
      setTailoredResumesMap(tailoredMap);
    } catch (error) {
      console.error("Error fetching candidate data:", error);
      const errorMsg = error.response?.data?.message ||
                       error.response?.data?.error ||
                       error.response?.data?.detail ||
                       error.message ||
                       "Failed to load candidate data";
      setErrorMessage(errorMsg);
    } finally {
      setIsLoadingCandidate(false);
    }
  }, [candidateId]);

  useEffect(() => {
    loadCandidate();
  }, [loadCandidate]);

  useEffect(() => {
    if (Object.keys(tailoredResumesMap).length > 0 && !activeTailoredResumeJdId && !selectedJobDescription) {
      const firstJdId = Object.keys(tailoredResumesMap)[0];
      const firstTailoredResume = tailoredResumesMap[firstJdId];
      if (firstTailoredResume && firstTailoredResume.resume && firstTailoredResume.resume.pdf_url) {
        setTailoredResume(firstTailoredResume.resume);
        setActiveTailoredResumeJdId(firstJdId);
      }
    }
  }, [tailoredResumesMap, activeTailoredResumeJdId, selectedJobDescription]);

  useEffect(() => {
    if (selectedJobDescription && Object.keys(tailoredResumesMap).length > 0) {
      const selectedJob = jobDescriptions.find((job) => job.id === selectedJobDescription);
      if (selectedJob) {
        const tailoredResumeItem = tailoredResumesMap[selectedJob.jd_id] || 
                                   tailoredResumesMap[selectedJob.jobdesc_id] || 
                                   tailoredResumesMap[selectedJobDescription];
        
        if (tailoredResumeItem && tailoredResumeItem.resume && tailoredResumeItem.resume.pdf_url) {
          setTailoredResume(tailoredResumeItem.resume);
          setActiveTailoredResumeJdId(tailoredResumeItem.jd_id || selectedJob.jd_id || selectedJobDescription);
        }
      }
    } else if (!selectedJobDescription) {
      if (Object.keys(tailoredResumesMap).length === 0) {
        setTailoredResume(null);
        setActiveTailoredResumeJdId(null);
      }
    }
  }, [selectedJobDescription, tailoredResumesMap, jobDescriptions]);

  useEffect(() => {
    const fetchTemplateId = async () => {
      setTemplateError("");
      try {
        const token = localStorage.getItem("jwtToken");
        if (!token) {
          return;
        }
        
        const templateResponse = await api.get(`${BASE_URL}/api/templates/active`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        
        if (templateResponse.data) {
          const templateIdValue = templateResponse.data._id || templateResponse.data.template_id;
          
          if (templateIdValue) {
            setTemplateId(templateIdValue);
          } else {
            setTemplateError("No template ID found in response");
          }
        } else {
          setTemplateError("No template available");
        }
      } catch (error) {
        console.error("Error fetching template ID:", error);
        const errorMsg = error.response?.data?.message || 
                         error.response?.data?.error ||
                         error.response?.data?.detail ||
                         error.message ||
                         "No template available. Please contact administrator.";
        setTemplateError(errorMsg);
      }
    };

    fetchTemplateId();
  }, []);

  const fetchJobDescriptions = useCallback(async () => {
    if (!actualCandidateId) {
      return;
    }
    
    const request = ++jobsRequestRef.current;
    setIsLoadingJobs(true);
    try {
      const token = localStorage.getItem("jwtToken");
      const response = await api.get(`${BASE_URL}/api/candidates/${actualCandidateId}/jds`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      const data = response.data || {};
      const tailoringJds = ["linked_jds", "jds", "available_jds"]
        .map((key) => asJobList(data[key]))
        .find((list) => list.length) || [];
      setJobDescriptions(tailoringJds.map((job) => ({
        id: job.jobdesc_id || job.jd_uuid,
        jobdesc_id: job.jobdesc_id,
        title: job.title || "Untitled Job",
        jd_id: job.jobdesc_id,
        jd_uuid: job.jd_uuid,
        ats_ref_id: job.ats_ref_id || "",
        parsed_skills: job.parsed_skills || [],
        status: job.status || "active",
      })));
      let linked = connectedJobsFrom(data);
      if (jobsRequestRef.current !== request) return;
      setConnectedJobs(linked);
      setHasLoadedJobs(true);
      setIsLoadingJobs(false);
      if (!linked.length) {
        const checks = await Promise.all(tailoringJds.map(async (job) => {
          const jobdescId = job.jobdesc_id || job.jd_id;
          if (!jobdescId) return null;
          try {
            const candidateResponse = await api.get(`${BASE_URL}/api/jd/${jobdescId}/candidates`);
            const raw = candidateResponse.data;
            const list = Array.isArray(raw)
              ? raw
              : Array.isArray(raw?.candidates)
                ? raw.candidates
                : Array.isArray(raw?.data)
                  ? raw.data
                  : Array.isArray(raw?.results)
                    ? raw.results
                    : Array.isArray(raw?.data?.candidates)
                      ? raw.data.candidates
                      : [];
            const found = list.some((person) => {
              const id = person.candidate_id || person.lead_id || person.id;
              return id != null && String(id) === String(actualCandidateId);
            });
            return found ? job : null;
          } catch (lookupError) {
            console.error("Error checking candidate job link:", lookupError);
            return null;
          }
        }));
        linked = checks.filter(Boolean).map(mapScheduleJob);
        if (jobsRequestRef.current !== request) return;
        if (linked.length) setConnectedJobs(linked);
      }
    } catch (error) {
      console.error("Error fetching job descriptions:", error);
      setJobDescriptions([]);
    } finally {
      setIsLoadingJobs(false);
      setHasLoadedJobs(true);
    }
  }, [actualCandidateId]);

  useEffect(() => {
    fetchJobDescriptions();
  }, [fetchJobDescriptions]);

  const originJob = useMemo(() => {
    if (!originJobId) return null;
    return jobDescriptions.find((job) => sameJobId(job, originJobId)) || null;
  }, [jobDescriptions, originJobId]);
  const originJdId = originJob ? originJob.jd_id || originJob.id : null;

  const scheduleJobs = useMemo(() => {
    const options = [];
    const add = (job) => {
      const id = job?.jobdesc_id || job?.jd_id || job?.id;
      if (!id || options.some((item) => item.id === id)) return;
      options.push({
        id,
        routeId: job.jd_uuid || job.routeId || job.jobdesc_id || job.jd_id || job.id,
        title: job.title || "Untitled Job",
      });
    };

    connectedJobs.forEach(add);
    if (originJob) add(originJob);
    else if (originJobId) {
      add({
        id: originJobId,
        jobdesc_id: originJobId,
        title: originJobTitle || "Untitled Job",
      });
    }

    const wantedIds = [
      ...candidateJobIds,
      ...Object.keys(tailoredResumesMap),
      ...Object.values(tailoredResumesMap).map(
        (item) => item?.jd_details?.jobdesc_id || item?.jd_details?.jd_id || item?.jd_id
      ),
    ];
    wantedIds.forEach((id) => {
      const match = jobDescriptions.find((job) => sameJobId(job, id));
      if (match) add(match);
    });

    return options;
  }, [connectedJobs, originJob, originJobId, originJobTitle, candidateJobIds, tailoredResumesMap, jobDescriptions]);

  const pendingInterviews = useMemo(
    () => interviews.filter((interview) => interview.isPending),
    [interviews]
  );

  // Every interview counts (completed and cancelled too); the list shows them all
  const interviewCount = candidateData?.interview_count ?? interviews.length;

  const isSchedulable = candidateData?.schedulable !== false;
  const jobsForSchedule = useMemo(() => {
    const options = [];
    const add = (job) => {
      const id = job?.jobdesc_id || job?.jd_id || job?.id;
      if (!id || options.some((item) => String(item.id) === String(id))) return;
      options.push({
        id,
        routeId: job.jd_uuid || job.routeId || id,
        title: job.title || job.jd_title || "Untitled Job",
      });
    };

    Object.values(tailoredResumesMap).forEach((item) => {
      const details = item?.jd_details || {};
      const rawId = details.jobdesc_id || details.jd_id || item?.jd_id;
      const match = jobDescriptions.find(
        (job) =>
          sameJobId(job, item?.jd_id) ||
          sameJobId(job, rawId) ||
          sameJobId(job, details.jd_uuid)
      );
      add({
        ...(match || {}),
        id: match?.jd_id || match?.jobdesc_id || rawId,
        jobdesc_id: match?.jobdesc_id || details.jobdesc_id || rawId,
        jd_id: match?.jd_id || details.jd_id || rawId,
        jd_uuid: match?.jd_uuid || details.jd_uuid,
        title: details.title || match?.title || "Untitled Job",
      });
    });

    connectedJobs.forEach(add);
    candidateJobIds.forEach((id) => {
      const match = jobDescriptions.find((job) => sameJobId(job, id));
      if (match) add(match);
    });
    if (originJob) add(originJob);
    else if (originJobId) {
      add({
        id: originJobId,
        jobdesc_id: originJobId,
        title: originJobTitle || "Untitled Job",
      });
    }

    return options;
  }, [tailoredResumesMap, jobDescriptions, connectedJobs, candidateJobIds, originJob, originJobId, originJobTitle]);
  const scheduleDisabledReason = !isSchedulable
    ? candidateData?.unschedulable_reason === "EMAIL_INVALID"
      ? "This candidate's email is invalid. Update it to schedule an interview."
      : "Add an email for this candidate to schedule an interview."
    : "";


  const handleInterviewScheduled = (interview) => {
    const scheduled = { ...interview, status: INTERVIEW_STATUS.SCHEDULED, isPending: false };
    const alreadyListed = interviews.some((existing) => existing.id && existing.id === scheduled.id);
    // Replaces the pending entry when a pending interview was scheduled
    setInterviews((prev) => [...prev.filter((existing) => existing.id !== scheduled.id), scheduled]);
    setCandidateData((prev) =>
      prev
        ? {
            ...prev,
            has_scheduled_interview: true,
            interview_count: alreadyListed
              ? prev.interview_count ?? interviews.length
              : (prev.interview_count ?? interviews.length) + 1,
          }
        : prev
    );
  };

  const checkActiveTemplate = async () => {
    if (templateId) {
      return true;
    }
    try {
      const userDataStringify = localStorage.getItem("user_data");
      if (!userDataStringify) {
        setShowNoTemplateModal(true);
        return false;
      }

      const token = localStorage.getItem("jwtToken");
      const response = await api.get(`${BASE_URL}/api/templates/active`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data && response.data.template_html) {
        return true;
      } else {
        setShowNoTemplateModal(true);
        return false;
      }
    } catch (error) {
      const isNotFound =
        error.response?.status === 404 ||
        error.response?.status === 400 ||
        error.response?.data?.message
          ?.toLowerCase()
          .includes("no active template") ||
        error.response?.data?.error
          ?.toLowerCase()
          .includes("no active template");

      if (isNotFound) {
        setShowNoTemplateModal(true);
        return false;
      } else {
        return true;
      }
    }
  };

  const generateStandardizedResume = async (switchTab = true) => {
    if (!actualCandidateId || !templateId) {
      if (!templateId) {
        setErrorMessage("No template available. Please contact administrator.");
      }
      return;
    }

    if (standardizedResume && standardizedResume.pdf_url) {
      if (switchTab) {
        setActiveTab("standardised");
      }
      return;
    }

    setIsGenerating(true);
    setErrorMessage("");

    try {
      const token = localStorage.getItem("jwtToken");
      if (!token) {
        throw new Error("No authentication token found");
      }
      
      const requestBody = {
        candidate_id: actualCandidateId,
        template_id: templateId,
      };
      
      const response = await api.post(
        `${BASE_URL}/api/resume/standardized`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data) {
        setStandardizedResume(response.data);
        const needsUpdate =
          response.data.is_latest_template === false ||
          response.data.is_latest_template === "false";
        setIsTemplateUpdateAvailable(needsUpdate);
        if (switchTab) {
          setActiveTab("standardised");
        }
      }
    } catch (error) {
      console.error("Error generating standardized resume:", error);
      const errorMsg = error.response?.data?.message || 
                       error.response?.data?.error ||
                       error.response?.data?.detail ||
                       error.message ||
                       "Failed to generate standardized resume. Please try again.";
      setErrorMessage(errorMsg);
    } finally {
      setIsGenerating(false);
    }
  };

  // Auto-generate standardized resume when page loads (if not already exists)
  useEffect(() => {
    if (actualCandidateId && templateId && !standardizedResume && !isGenerating) {
      generateStandardizedResume(false); // Don't switch tab automatically
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualCandidateId, templateId]);

  const generateTailoredResume = async () => {
    if (!actualCandidateId || !templateId || !selectedJobDescription) {
      if (!templateId) {
        setErrorMessage("No template available. Please contact administrator.");
      }
      return;
    }

    const selectedJob = jobDescriptions.find((job) => job.id === selectedJobDescription);
    if (!selectedJob) {
      return;
    }
    
    const jobdescId = selectedJob.jd_id || selectedJob.jobdesc_id || selectedJob.id;
    
    const existingTailoredResumeItem = tailoredResumesMap[selectedJob.jd_id] || 
                                       tailoredResumesMap[selectedJob.jobdesc_id] || 
                                       tailoredResumesMap[selectedJobDescription];
    if (existingTailoredResumeItem && existingTailoredResumeItem.resume && existingTailoredResumeItem.resume.pdf_url) {
      setTailoredResume(existingTailoredResumeItem.resume);
      setActiveTailoredResumeJdId(existingTailoredResumeItem.jd_id || jobdescId);
      setActiveTab("tailored");
      return;
    }

    setIsGeneratingTailored(true);
    setErrorMessage("");

    try {
      const token = localStorage.getItem("jwtToken");
      if (!token) {
        throw new Error("No authentication token found");
      }
      
      const requestBody = {
        candidate_id: actualCandidateId,
        jd_id: jobdescId,
        template_id: templateId,
      };
      
      const response = await api.post(
        `${BASE_URL}/api/resume/tailored`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data) {
        const generatedResume = response.data;
        const currentJobdescId = jobdescId;
        
        setTailoredResume(generatedResume);
        setTailoredResumesMap(prev => ({
          ...prev,
          [jobdescId]: {
            resume: generatedResume,
            jd_details: {
              title: selectedJob.title || selectedJob.ats_title || "Untitled Job",
              required_skills: selectedJob.required_skills || [],
              short_description: selectedJob.short_description || "",
            },
            jd_id: jobdescId,
          },
        }));
        setActiveTailoredResumeJdId(jobdescId);
        setActiveTab("tailored");
        await fetchJobDescriptions();
        setSelectedJobDescription("");
      }
    } catch (error) {
      console.error("Error generating tailored resume:", error);
      const errorMsg = error.response?.data?.message || 
                       error.response?.data?.error ||
                       error.response?.data?.detail ||
                       error.message ||
                       "Failed to generate tailored resume. Please try again.";
      setErrorMessage(errorMsg);
    } finally {
      setIsGeneratingTailored(false);
    }
  };

  const handleGenerateTailoredResume = () => {
    generateTailoredResume();
  };

  const handleAddTemplate = () => {
    setShowNoTemplateModal(false);
    navigate("/standardized-template");
  };

  const updateToLatestTemplate = async () => {
    if (!actualCandidateId) {
      setErrorMessage("Candidate ID not found");
      return;
    }

    setIsUpdatingTemplate(true);
    setErrorMessage("");

    try {
      const token = localStorage.getItem("jwtToken");
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await api.post(
        `${BASE_URL}/api/resume/update-to-latest-template`,
        {
          candidate_id: actualCandidateId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data) {
        const updateResponse = response.data;
        const standardizedRegenerated = updateResponse.standardized_resume_regenerated || false;
        const tailoredResumesUpdated = updateResponse.tailored_resumes || [];

        const refreshResponse = await api.get(
          `${BASE_URL}/api/single-candidate/${candidateId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = refreshResponse.data || {};
        const standardizedResumeData = data.standardized_resume || null;
        const tailoredResumesData = data.tailored_resumes || [];

        if (standardizedRegenerated && standardizedResumeData && standardizedResumeData.pdf_url) {
          setStandardizedResume(standardizedResumeData);
          // Check if template update is still available after update based on is_latest_template flag
          const needsUpdate =
            standardizedResumeData.is_latest_template === false ||
            standardizedResumeData.is_latest_template === "false";
          setIsTemplateUpdateAvailable(needsUpdate);
        }

        const updatedJdIds = tailoredResumesUpdated
          .filter(tr => tr.regenerated === true)
          .map(tr => tr.jd_id);

        const updatedTailoredMap = { ...tailoredResumesMap };
        
        tailoredResumesData.forEach((item) => {
          if (item.jd_id && item.resume && item.resume.pdf_url) {
            updatedTailoredMap[item.jd_id] = {
              resume: item.resume,
              jd_details: item.jd_details || {},
              jd_id: item.jd_id,
            };
            if (activeTailoredResumeJdId === item.jd_id && updatedJdIds.includes(item.jd_id)) {
              setTailoredResume(item.resume);
            }
          }
        });

        setTailoredResumesMap(updatedTailoredMap);
      }
    } catch (error) {
      console.error("Error updating to latest template:", error);
      const errorMsg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.response?.data?.detail ||
        error.message ||
        "Failed to update template. Please try again.";
      setErrorMessage(errorMsg);
    } finally {
      setIsUpdatingTemplate(false);
    }
  };

  return (
    <div
      className="flex flex-col w-full h-full"
      style={{
        backgroundColor: theme.palette.primary.v2,
        color: theme.palette.text.main,
      }}
    >
      <NoActiveTemplateModal
        isOpen={showNoTemplateModal}
        onClose={() => setShowNoTemplateModal(false)}
        onAddTemplate={handleAddTemplate}
      />
      {/* Header */}
      <div
        className="flex flex-wrap justify-between items-center gap-3 px-4 sm:px-6 py-5 border-b max-sm:pl-12"
        style={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.text.main,
          borderColor: theme.palette.secondary.v4,
        }}
      >
        <div className="flex items-center gap-3">
          <IconButton
            onClick={() => {
              // Use the previous route from location state if available, otherwise fall back to navigate(-1)
              const previousRoute = location.state?.from;
              if (previousRoute) {
                navigate(previousRoute);
              } else {
                navigate(-1);
              }
            }}
            aria-label="Go back"
            sx={{
              color: theme.palette.text.main,
              padding: "0px",
              borderRadius: "4px",
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <h1 className="text-xl md:text-2xl font-semibold truncate">
                {candidateData ? (
                  candidateData.name ||
                  `${candidateData.first_name || ""} ${candidateData.last_name || ""}`.trim() ||
                  "Unknown Candidate"
                ) : (
                  "Loading"
                )}
              </h1>
              {candidateData && (
                <IconButton
                  onClick={() => setIsEditCandidateOpen(true)}
                  aria-label="Edit candidate"
                  sx={{
                    color: theme.palette.text.main,
                    padding: "4px",
                  }}
                >
                  <PencilSvgIcon />
                </IconButton>
              )}
            </div>
            {candidateData && (
              <p className="text-sm truncate" style={{ color: theme.palette.text.secondary }}>
                {candidateData.email || "No email"}
              </p>
            )}
          </div>
        </div>
        {candidateData && hasLoadedInterviews && (
          <div className="flex items-center justify-end flex-wrap gap-3">
            {interviewCount > 0 && (
              <Button
                onClick={() => setIsInterviewListOpen(true)}
                sx={{
                  backgroundColor: theme.palette.info.main,
                  color: theme.palette.text.light,
                  textTransform: "none",
                }}
              >
                View interviews ({interviewCount})
              </Button>
            )}
            {hasLoadedJobs && jobsForSchedule.length > 0 && (
              <Tooltip title={scheduleDisabledReason} arrow>
                {/* span keeps the tooltip working while the button is disabled */}
                <span>
                  <Button
                    {...scheduleGate.controlProps}
                    onClick={scheduleGate.guard(() => setIsScheduleModalOpen(true))}
                    disabled={!!scheduleDisabledReason}
                    sx={{
                      backgroundColor: theme.palette.info.main,
                      color: theme.palette.text.light,
                      "&.Mui-disabled": {
                        backgroundColor: theme.palette.secondary.v3,
                        color: theme.palette.text.light,
                      },
                    }}
                  >
                    Schedule interview
                  </Button>
                  {scheduleGate.hint}
                </span>
              </Tooltip>
            )}
          </div>
        )}
      </div>

      <EditCandidateModal
        isOpen={isEditCandidateOpen}
        onClose={() => setIsEditCandidateOpen(false)}
        candidate={{
          candidate_id: actualCandidateId || candidateData?.candidate_id,
          name:
            candidateData?.name ||
            `${candidateData?.first_name || ""} ${candidateData?.last_name || ""}`.trim(),
          email: candidateData?.email || "",
        }}
        onSaved={loadCandidate}
      />

      <ScheduleInterviewModal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        candidate={{ candidate_id: actualCandidateId, name: candidateData?.name, email: candidateData?.email }}
        candidateJobs={jobsForSchedule}
        defaultJobId={
          (jobsForSchedule.length === 1
            ? jobsForSchedule[0].id
            : jobsForSchedule.some((job) => job.id === originJdId)
              ? originJdId
              : "") || ""
        }
        pendingInterviews={pendingInterviews}
        onScheduled={(saved = []) => saved.forEach(handleInterviewScheduled)}
      />

      <InterviewListModal
        isOpen={isInterviewListOpen}
        onClose={() => setIsInterviewListOpen(false)}
        interviews={interviews}
        jobs={scheduleJobs}
        interviewCount={candidateData?.interview_count}
        candidate={{ name: candidateData?.name, email: candidateData?.email }}
      />

      {/* Main Content */}
      <div className="overflow-auto h-[calc(100% - 75px)]">
        {isInitialLoading ? (
          <div className="mx-auto px-6 sm:px-12 lg:px-16 text-center mt-16 h-[calc(100dvh-72px)] w-full">
            <div className="spinner-border animate-spin inline-block w-8 h-8 border-2 border-t-4 border-black rounded-full" />
            <p>Loading candidate details...</p>
          </div>
        ) : (
        candidateData &&
          (() => {
            const candidateJob = candidateData.job_id
              ? jobDescriptions.find(
                  (job) =>
                    job.id === candidateData.job_id ||
                    job.jobdesc_id === candidateData.job_id ||
                    job.jd_id === candidateData.job_id ||
                    job.jd_uuid === candidateData.job_id
                )
              : null;
            // const designation = candidateJob?.title || candidateData.job_title || "No designation"; // Unused - removed to fix lint warning

            if (!candidateData.resume_skills || candidateData.resume_skills.length === 0) {
              return null;
            }

            return (
              <div
                className="mx-4 sm:mx-6 mt-4 mb-4 p-3 sm:p-4 rounded-lg border"
                style={{
                  backgroundColor: theme.palette.primary.main,
                  borderColor: theme.palette.secondary.v4,
                }}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex-1">
                    <Box
                      ref={skillsContainerRef}
                      sx={{
                        width: "100%",
                        "& > div": {
                          padding: "0 !important",
                          width: "100% !important",
                        },
                        "& .MuiTableCell-root": {
                          display: "block !important",
                          padding: "0 !important",
                          border: "none !important",
                          width: "100% !important",
                        },
                        "& > div > div": {
                          justifyContent: "flex-start !important",
                          "@media (max-width: 640px)": {
                            justifyContent: "flex-start !important",
                          },
                        },
                        "& .MuiChip-root:not([aria-expanded])": {
                          border: "none !important",
                        },
                      }}
                    >
                      <SkillChipsPopover
                        skills={candidateData.resume_skills}
                        width="100%"
                        columnWidth={containerWidth}
                        setMobileVisibleSkills={false}
                      />
                    </Box>
                  </div>
                </div>
              </div>
            );
          })())}

{(() => {
            const isUpdateAvailable = standardizedResume?.is_latest_template === false || standardizedResume?.is_latest_template === "false";
            
            return isUpdateAvailable && (
              <div
                className="mx-4 sm:mx-6 mb-4 rounded-lg border p-4 flex items-center justify-between"
                style={{
                  backgroundColor: "#EFF6FF",
                  borderColor: "#93C5FD",
                }}
              >
                <div className="flex items-center gap-3 flex-1">
                  <svg
                    className="w-5 h-5 flex-shrink-0"
                    style={{ color: "#3B82F6" }}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p
                    className="text-sm font-medium"
                    style={{ color: "#1E40AF" }}
                  >
                    Template update available
                  </p>
                </div>
                <Button
                  onClick={updateToLatestTemplate}
                  disabled={isUpdatingTemplate}
                  size="small"
                  sx={{
                    backgroundColor: theme.palette.info.main,
                    color: theme.palette.text.light,
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: theme.palette.info.dark,
                    },
                    "&.Mui-disabled": {
                      backgroundColor: theme.palette.secondary.v3,
                      color: theme.palette.text.light,
                    },
                  }}
                >
                  {isUpdatingTemplate ? "Updating..." : "Update"}
                </Button>
              </div>
            );
          })()}

        <div
          className="flex-1 m-6 max-md:mx-4 max-md:mt-4 mb-4 h-full min-h-[632px] md:min-h-[664px]"
          style={{ minWidth: 0 }}
        >
          {/* Template Update Banner - Shows above resume display area */}
         
          <div className="flex max-md:flex-col flex-row gap-6 h-full">
            {/* AI Resume Tailoring - Shows first on mobile, right side on desktop */}
            <div
              className="md:sticky md:top-0 w-full h-fit md:w-4/12 border rounded-lg md:overflow-auto p-4 md:p-6 order-1 md:order-2"
              style={{
                backgroundColor: theme.palette.primary.main,
                borderColor: theme.palette.secondary.v4,
              }}
            >
              <h2
                className="text-lg font-semibold mb-4"
                style={{ color: theme.palette.text.main }}
              >
                AI Resume Tailoring
              </h2>

              {/* Template Error Message */}
              {templateError && (
                <div
                  className="mb-4 rounded-lg border p-3 text-sm"
                  style={{
                    backgroundColor: "#FEF2F2",
                    borderColor: "#FCA5A5",
                    color: "#DC2626",
                  }}
                >
                  {templateError}
                </div>
              )}

              {/* Select Job Description */}
              <div className="mb-4">
                <label
                  className="block text-sm font-medium mb-2"
                  style={{ color: theme.palette.text.main }}
                >
                  Select Job Description
                </label>
                <FormControl fullWidth size="small">
                  <Select
                    value={selectedJobDescription}
                    onChange={(e) => {
                      const newJobId = e.target.value;
                      setSelectedJobDescription(newJobId);
                    }}
                    displayEmpty
                    disabled={isLoadingJobs || jobDescriptions.length === 0}
                    sx={{
                      color: theme.palette.text.main,
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: "4px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.palette.secondary.v4,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.palette.secondary.v4,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme.palette.info.main,
                      },
                    }}
                  >
                    <MenuItem value="" disabled>
                      {isLoadingJobs
                        ? "Loading..."
                        : jobDescriptions.length === 0
                        ? "No job descriptions available"
                        : "Select an option"}
                    </MenuItem>
                    {jobDescriptions.map((job) => (
                      <MenuItem key={job.id} value={job.id}>
                        {job.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              {/* Generate Tailored Resume Button */}
              {(() => {
                const selectedJob = jobDescriptions.find(
                  (job) => job.id === selectedJobDescription
                );
                const existingTailoredResumeItem = selectedJob
                  ? tailoredResumesMap[selectedJob.jd_id] ||
                    tailoredResumesMap[selectedJob.jobdesc_id] ||
                    tailoredResumesMap[selectedJobDescription]
                  : null;
                const hasExistingTailoredResume =
                  existingTailoredResumeItem &&
                  existingTailoredResumeItem.resume &&
                  existingTailoredResumeItem.resume.pdf_url;
                const isDisabled =
                  isGeneratingTailored ||
                  !templateId ||
                  !actualCandidateId ||
                  !selectedJobDescription ||
                  jobDescriptions.length === 0;

                return (
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleGenerateTailoredResume();
                    }}
                    disabled={isDisabled}
                    className="!h-auto !text-base"
                    sx={{
                      backgroundColor: theme.palette.info.main,
                      color: theme.palette.text.light,
                      textTransform: "none",
                      borderRadius: "4px",
                      marginBottom: 3,
                      padding: "4px 10px",
                    }}
                  >
                    {isGeneratingTailored
                      ? "Generating..."
                      : hasExistingTailoredResume
                      ? "View Tailored Resume"
                      : "Generate Tailored Resume"}
                  </Button>
                );
              })()}

              {/* Error Message */}
              {errorMessage && (
                <div
                  className="mb-4 rounded-lg border p-3 text-sm"
                  style={{
                    backgroundColor: "#FEF2F2",
                    borderColor: "#FCA5A5",
                    color: "#DC2626",
                  }}
                >
                  {errorMessage}
                </div>
              )}

              {/* Divider */}
              <div
                className="mb-4"
                style={{
                  borderTop: `1px solid ${theme.palette.secondary.v4}`,
                }}
              />

              {/* Generated Versions */}
              <div>
                <h3
                  className="text-lg font-semibold mb-4"
                  style={{ color: theme.palette.text.main }}
                >
                  Generated Versions
                </h3>

                {/* Tabs */}
                <div className="flex flex-col gap-2">
                  {/* Display all tailored resumes from API */}
                  {Object.values(tailoredResumesMap).map(
                    (tailoredResumeItem) => {
                      const isActive =
                        activeTab === "tailored" &&
                        activeTailoredResumeJdId === tailoredResumeItem.jd_id;
                      const jobTitle =
                        tailoredResumeItem.jd_details?.title ||
                        jobDescriptions.find(
                          (job) =>
                            job.jd_id === tailoredResumeItem.jd_id ||
                            job.id === tailoredResumeItem.jd_id
                        )?.title ||
                        "Tailored Resume";

                      return (
                        <button
                          key={tailoredResumeItem.jd_id}
                          onClick={() => {
                            setTailoredResume(tailoredResumeItem.resume);
                            setActiveTailoredResumeJdId(
                              tailoredResumeItem.jd_id
                            );
                            setActiveTab("tailored");
                          }}
                          className="w-full py-1 px-2.5 rounded text-sm font-medium transition-colors flex items-center justify-between flex-wrap gap-1"
                          style={{
                            backgroundColor: isActive
                              ? theme.palette.info.main
                              : theme.palette.primary.main,
                            color: isActive
                              ? theme.palette.text.light
                              : theme.palette.text.main,
                            border: `1px solid ${
                              isActive
                                ? theme.palette.info.main
                                : theme.palette.secondary.v4
                            }`,
                            cursor: "pointer",
                          }}
                        >
                          <span className="text-base font-normal text-left">
                            {jobTitle}
                          </span>
                          <span className="text-sm font-normal text-right ml-auto">
                            AI-generated
                          </span>
                        </button>
                      );
                    }
                  )}

                  {/* Show generating state only if this JD does not already have a resume */}
                  {(() => {
                    const selectedJob = selectedJobDescription
                      ? jobDescriptions.find((job) => job.id === selectedJobDescription)
                      : null;
                    const existingGeneratingResume =
                      selectedJob &&
                      (tailoredResumesMap[selectedJob.jd_id] ||
                        tailoredResumesMap[selectedJob.jobdesc_id] ||
                        tailoredResumesMap[selectedJobDescription]);

                    return (
                      isGeneratingTailored &&
                      selectedJobDescription &&
                      !existingGeneratingResume && (
                    <button
                      disabled
                      className="w-full py-1 px-2.5 rounded text-sm font-medium transition-colors flex items-center justify-between flex-wrap gap-1"
                      style={{
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.text.main,
                        border: `1px solid ${theme.palette.secondary.v4}`,
                        opacity: 0.7,
                        cursor: "not-allowed",
                      }}
                    >
                      <span className="text-base font-normal text-left flex items-center gap-2">
                        <CircularProgress
                          size={16}
                          style={{ color: theme.palette.text.main }}
                        />
                        {jobDescriptions.find(
                          (job) => job.id === selectedJobDescription
                        )?.title || "Tailored Resume"}
                      </span>
                      <span className="text-sm font-normal text-left">
                        Generating...
                      </span>
                    </button>
                      )
                    );
                  })()}
                  <button
                    onClick={async () => {
                      if (!standardizedResume && !isGenerating) {
                        // Check if template exists before generating
                        const hasTemplate = await checkActiveTemplate();
                        if (hasTemplate && templateId) {
                          generateStandardizedResume(); // This will switch tab automatically
                        }
                      } else {
                        setActiveTab("standardised"); // Switch tab if already generating or exists
                      }
                    }}
                    className="w-full py-1 px-2.5 rounded text-sm font-medium transition-colors flex items-center justify-between flex-wrap gap-1"
                    style={{
                      backgroundColor:
                        activeTab === "standardised"
                          ? theme.palette.info.main
                          : theme.palette.primary.main,
                      color:
                        activeTab === "standardised"
                          ? theme.palette.text.light
                          : theme.palette.text.main,
                      border: `1px solid ${
                        activeTab === "standardised"
                          ? theme.palette.info.main
                          : theme.palette.secondary.v4
                      }`,
                    }}
                  >
                    <span className="text-base font-normal text-left">
                      Standardized Format
                    </span>
                    <span className="text-sm font-normal text-right ml-auto">
                      AI-generated
                    </span>
                  </button>
                  <button
                    onClick={() => setActiveTab("resume")}
                    className="w-full py-1 px-2.5 rounded text-sm font-medium transition-colors flex items-center justify-between"
                    style={{
                      backgroundColor:
                        activeTab === "resume"
                          ? theme.palette.info.main
                          : theme.palette.primary.main,
                      color:
                        activeTab === "resume"
                          ? theme.palette.text.light
                          : theme.palette.text.main,
                      border: `1px solid ${
                        activeTab === "resume"
                          ? theme.palette.info.main
                          : theme.palette.secondary.v4
                      }`,
                    }}
                  >
                    <span className="text-base font-normal text-left">
                      Resume
                    </span>
                    <span className="text-sm font-normal text-right ml-auto">
                      Original
                    </span>
                  </button>
                </div>
              </div>
            </div>

            {/* Resume Display - Shows below on mobile, left side on desktop */}
            <div
              className="flex-1 md:overflow-hidden p-4 md:p-8 order-2 md:order-1 h-full md:w-8/12 rounded-lg border min-h[632px] md:min-h-[600px]"
              style={{
                backgroundColor: "#ffffff",
                minWidth: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                position: "relative",
                borderColor: theme.palette.secondary.v4,
              }}
            >
              <div
                className="w-full max-w-4xl h-full candidate-pdf overflow-hidden"
                style={{
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  boxSizing: "border-box",
                  margin: "0 auto",
                  position: "relative",
                }}
              >
                {isGenerating && activeTab === "standardised" ? (
                  <Box
                    className="flex flex-col items-center justify-center min-h-[600px] md:min-h-[550px]"
                    style={{ color: theme.palette.text.main }}
                  >
                    <CircularProgress
                      size={60}
                      style={{
                        color: theme.palette.info.main,
                        marginBottom: "16px",
                      }}
                    />
                    <p
                      style={{
                        color: theme.palette.text.main,
                        fontSize: "16px",
                      }}
                    >
                      Generating standardized resume...
                    </p>
                  </Box>
                ) : activeTab === "tailored" && isGeneratingTailored ? (
                  <Box
                    className="flex flex-col items-center justify-center min-h-[600px] md:min-h-[550px]"
                    style={{ color: theme.palette.text.main }}
                  >
                    <CircularProgress
                      size={60}
                      style={{
                        color: theme.palette.info.main,
                        marginBottom: "16px",
                      }}
                    />
                    <p
                      style={{
                        color: theme.palette.text.main,
                        fontSize: "16px",
                      }}
                    >
                      Generating tailored resume...
                    </p>
                  </Box>
                ) : activeTab === "tailored" && tailoredResume?.pdf_url ? (
                  <div
                    style={{
                      width: "100%",
                      maxWidth: "100%",
                      position: "relative",
                      minHeight: "600px",
                      boxSizing: "border-box",
                    }}
                  >
                    <iframe
                      src={getResumeUrl(tailoredResume.pdf_url)}
                      className="w-full border rounded-lg"
                      style={{
                        borderColor: theme.palette.secondary.v4,
                        backgroundColor: "#ffffff",
                        display: "block",
                        width: "100%",
                        height: "100%",
                        minHeight: "600px",
                        boxSizing: "border-box",
                      }}
                      title="Tailored Resume"
                      onError={() => {
                        // Preview failed
                      }}
                    />
                  </div>
                ) : activeTab === "standardised" &&
                  standardizedResume?.pdf_url ? (
                  <div
                    style={{
                      width: "100%",
                      maxWidth: "100%",
                      position: "relative",
                      minHeight: "600px",
                      boxSizing: "border-box",
                    }}
                  >
                    <iframe
                      src={getResumeUrl(standardizedResume.pdf_url)}
                      className="w-full border rounded-lg"
                      style={{
                        borderColor: theme.palette.secondary.v4,
                        backgroundColor: "#ffffff",
                        display: "block",
                        width: "100%",
                        height: "100%",
                        minHeight: "600px",
                        boxSizing: "border-box",
                      }}
                      title="Standardized Resume"
                      onError={() => {
                        // Preview failed
                      }}
                    />
                  </div>
                ) : activeTab === "resume" ? (
                  resumeUrl ? (
                    <div
                      style={{
                        width: "100%",
                        maxWidth: "100%",
                        position: "relative",
                        minHeight: "600px",
                        boxSizing: "border-box",
                      }}
                    >
                      <iframe
                        src={resumeUrl}
                        className="w-full border rounded-lg"
                        style={{
                          borderColor: theme.palette.secondary.v4,
                          backgroundColor: "#ffffff",
                          display: "block",
                          width: "100%",
                          height: "100%",
                          minHeight: "600px",
                          boxSizing: "border-box",
                        }}
                        title="Candidate Resume"
                        onError={() => {
                          // Preview failed
                        }}
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center min-h-[600px]">
                      <p style={{ color: theme.palette.text.main }}>
                        {isLoadingCandidate
                          ? "Loading resume..."
                          : "No resume available"}
                      </p>
                    </div>
                  )
                ) : (
                  <div className="flex items-center justify-center min-h-[600px]">
                    <p style={{ color: theme.palette.text.main }}>
                      {activeTab === "tailored"
                        ? "No tailored resume available. Please select a job description and generate one."
                        : activeTab === "standardised"
                        ? "No standardized resume available. Please generate one."
                        : "No resume available"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateDetailPage;
